// liquidNeon.ts — BETA 22.95
//
// "LIQUID NEON" pick-screen backdrop (Dylan: "the electric behind the cards
// when they pop up looks lame… think liquid neon"). Replaces lightningVfx on
// the level-up / shrine / wild-card / ultra screens.
//
// Look: slow, heavy blobs of molten neon drifting in the dark behind the
// cards — like ink drops of light in oil. Two-tone palette per screen so each
// pick screen keeps its identity (blue = level-up, purple = shrine, gold =
// wild cards). Everything is radial gradients composited 'lighter': natively
// smooth, no per-frame filter blur, no shadowBlur (13.70 perf law).
//
// All motion derives from performance.now() with irrational-ish period
// ratios so the pattern never visibly loops.

export const NEON_BLUE:   [string, string] = ['59,130,246', '34,211,238'];   // level-up
export const NEON_PURPLE: [string, string] = ['168,85,247', '99,102,241'];   // shrine
export const NEON_GOLD:   [string, string] = ['251,191,36', '249,115,22'];   // wild cards / ultra

interface Blob {
  cx: number; cy: number;      // home position (fractions of w/h)
  r: number;                   // radius as fraction of min(w,h)
  ax: number; ay: number;      // drift amplitude (fractions)
  px: number; py: number;      // drift periods (seconds)
  ph: number;                  // phase offset
  tone: 0 | 1;                 // palette index
}

// Hand-placed so the center card band stays darkest (cards must pop).
const BLOBS: Blob[] = [
  { cx: 0.12, cy: 0.22, r: 0.34, ax: 0.05, ay: 0.04, px: 11.3, py: 8.7,  ph: 0.0, tone: 0 },
  { cx: 0.88, cy: 0.24, r: 0.30, ax: 0.04, ay: 0.05, px: 9.1,  py: 12.9, ph: 1.7, tone: 1 },
  { cx: 0.20, cy: 0.86, r: 0.38, ax: 0.05, ay: 0.03, px: 13.7, py: 10.3, ph: 3.1, tone: 1 },
  { cx: 0.82, cy: 0.88, r: 0.36, ax: 0.06, ay: 0.04, px: 10.9, py: 9.7,  ph: 4.4, tone: 0 },
  { cx: 0.50, cy: 0.04, r: 0.26, ax: 0.08, ay: 0.02, px: 15.1, py: 7.9,  ph: 2.2, tone: 0 },
  { cx: 0.50, cy: 0.98, r: 0.28, ax: 0.07, ay: 0.02, px: 8.3,  py: 14.1, ph: 5.3, tone: 1 },
];

/**
 * Draw the liquid-neon wash. Call AFTER the dark overlay fill, BEFORE cards.
 * @param palette one of NEON_BLUE / NEON_PURPLE / NEON_GOLD ("r,g,b" pairs)
 */
export function drawLiquidNeon(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  palette: [string, string],
): void {
  const t = performance.now() / 1000;
  const base = Math.min(w, h);

  ctx.save();
  ctx.globalCompositeOperation = 'lighter';

  for (const b of BLOBS) {
    const x = (b.cx + b.ax * Math.sin((t + b.ph) * (Math.PI * 2) / b.px)) * w;
    const y = (b.cy + b.ay * Math.cos((t + b.ph) * (Math.PI * 2) / b.py)) * h;
    const r = b.r * base * (1 + 0.06 * Math.sin((t + b.ph * 2.3) / 3.1));
    const rgb = palette[b.tone];
    // molten core → soft falloff → nothing. Alphas kept low so the wash
    // reads as atmosphere, never competes with the card glows.
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0.0, `rgba(${rgb},0.135)`);
    g.addColorStop(0.45, `rgba(${rgb},0.06)`);
    g.addColorStop(1.0, `rgba(${rgb},0)`);
    ctx.fillStyle = g;
    ctx.fillRect(x - r, y - r, r * 2, r * 2);
  }

  // a thin bright "surface streak" that slowly sweeps — the liquid highlight
  const sy = h * (0.5 + 0.42 * Math.sin(t / 9.4));
  const sg = ctx.createLinearGradient(0, sy - h * 0.05, 0, sy + h * 0.05);
  sg.addColorStop(0, `rgba(${palette[0]},0)`);
  sg.addColorStop(0.5, `rgba(${palette[1]},0.05)`);
  sg.addColorStop(1, `rgba(${palette[0]},0)`);
  ctx.fillStyle = sg;
  ctx.fillRect(0, sy - h * 0.05, w, h * 0.1);

  ctx.restore();
}
